import { useState, useEffect, useRef } from 'react'
import { apiClient } from '../utils/api'
import { InstancesData } from '../types/api'

interface UseApiDataReturn {
  data: InstancesData | null
  loading: boolean
  error: string
  refreshData: () => Promise<void>
  getAdditionalData: () => Promise<void>
}

export function useApiData(token: string | null, raApi: string | null): UseApiDataReturn {
  const [data, setData] = useState<InstancesData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const lastCallRef = useRef<number>(0)
  
  // Configuración de debouncing
  const MIN_INTERVAL_MS = 2000 // 2 segundos entre llamadas

  const handleApiCall = async (endpoint: 'data' | 'data2') => {
    if (!token || !raApi) return

    // Debouncing: verificar tiempo desde última llamada
    const now = Date.now()
    const timeSinceLastCall = now - lastCallRef.current
    
    if (timeSinceLastCall < MIN_INTERVAL_MS) {
      // Demasiado pronto, ignorar la llamada
      return
    }
    
    lastCallRef.current = now
    setLoading(true)
    setError('')

    try {
      const response = endpoint === 'data' 
        ? await apiClient.getData(token, raApi)
        : await apiClient.getData2(token, raApi)
      
      if (response.data) {
        setData(response.data)
      } else {
        setError(response.error || 'Error fetching data')
        setData(null)
      }
    } catch (err) {
      // Manejar errores de rate limiting del backend
      if (err instanceof Error && err.message.includes('429')) {
        setError('Too many requests. Please wait before trying again.')
      } else {
        setError('Connection error')
      }
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const refreshData = async () => {
    await handleApiCall('data')
  }

  const getAdditionalData = async () => {
    await handleApiCall('data2')
  }

  // Auto-load initial data when token and raApi are available
  useEffect(() => {
    if (token && raApi) {
      handleApiCall('data')
    }
  }, [token, raApi])

  return {
    data,
    loading,
    error,
    refreshData,
    getAdditionalData
  }
}
